<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-stamp-config">
        <i class="far fa-stamp"></i> Stamp Config
      </Tab>
      <Tab tabid="tab-settings">
        <i class="far fa-cogs"></i> Settings
      </Tab>
      <Tab tabid="tab-activity-log">
        <i class="far fa-history"></i> Activity Log
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-stamp-config">
        <DataPageArea title="Stamp Configuration" subTitle="Configuration of the order stamp applied to toughened glass">
          <template slot="actionArea">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderConfiguration)" class="float-right">
              <DropdownMenuItem @click="editStampConfig" icon="fa-pencil">Edit Stamp Config</DropdownMenuItem>
            </ActionDropMenu>
          </template>
          <template slot="dataArea">
            <DataItem title="Stamp Horizontal Position">{{formatLookupName(stampConfig.stampAlignHorz, AlignmentTypeLookup)}}</DataItem>
            <DataItem title="Stamp Vertical Position">{{formatLookupName(stampConfig.stampAlignVert, AlignmentTypeLookup)}}</DataItem>
            <DataItem title="Stamp Required Stock">
              <LinkStock v-if="stampConfig.stampRequiredStock" :id="stampConfig.stampRequiredStockId" :name="stampConfig.stampRequiredStock.name" />
              <span v-if="!stampConfig.stampRequiredStock">None</span>
            </DataItem>
            <DataItem title="Stamp Not Required Stock">
              <LinkStock v-if="stampConfig.stampNotRequiredStock" :id="stampConfig.stampNotRequiredStockId" :name="stampConfig.stampNotRequiredStock.name" />
              <span v-if="!stampConfig.stampNotRequiredStock">None</span>
            </DataItem>
            <DataItem title="Stamp Image">
              <img v-if="stampConfig.stampImageData" :src="stampConfig.stampImageData" class="stamp-preview-image" />
            </DataItem>
            <DataItem title="Auto Apply">
              {{formatBoolean(stampConfig.isAutoApply)}}
            </DataItem>
          </template>
          
        </DataPageArea>
      </TabPage>

      <TabPage tabid="tab-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderConfiguration) && !generalConfig.editing" class="float-right">
              <DropdownMenuItem @click="generalConfig.editing = true" icon="fa-pencil">Edit Order Settings</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Order Settings</ContentTitle>
            <ContentSubTitle>General settings for the Orders module</ContentSubTitle>
          </div>
        </div>
        <Spinner v-if="loading" />
        <FormGroup :labelColumnSize="6" :form="generalConfig.form" v-show="!loading">
          <div class="row mt-2">
            <div class="col-lg-4">
              <FormAccountSetting label="NCR Additional Delivery Stock Item" helpDefault="None">
                <template v-slot:helpContent>
                  <p>This stock item will be automatically inserted into NCR orders with denied items and an additional delivery charge applied.</p>
                </template>
                <template v-slot:entry>
                  <LinkStock v-if="!generalConfig.editing" :id="getConfigSettingInt(AccountSettingKeys.orderNCRDeliveryFeeStockId)" :name="getLookupName(generalStockListLookup, getConfigSettingInt(AccountSettingKeys.orderNCRDeliveryFeeStockId))" />
                  <FormInputStockCode v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderNCRDeliveryFeeStockId)" @input="setConfigSetting(AccountSettingKeys.orderNCRDeliveryFeeStockId, $event)" :stockType="InventoryStockTypes.general" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Cancelled Order Entry Fee Stock Item" helpDefault="None">
                <template v-slot:helpContent>
                  <p>This stock item will be used when adding fees to cover order entry costs for cancelled orders.<br />
                  If this stock uses the Time unit of measure, the fee units will be based on the duration of the order entry process</p>
                  <p>If not set, the order entry fees will need to be manually entered when cancelling orders</p>
                </template>
                <template v-slot:entry>
                  <LinkStock v-if="!generalConfig.editing" :id="getConfigSettingInt(AccountSettingKeys.orderEntryCancellationFeeStockId)" :name="getLookupName(generalStockListLookup, getConfigSettingInt(AccountSettingKeys.orderEntryCancellationFeeStockId))" />
                  <FormInputStockCode v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderEntryCancellationFeeStockId)" @input="setConfigSetting(AccountSettingKeys.orderEntryCancellationFeeStockId, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Minimum Charge Fee Stock Item" helpDefault="None">
                <template v-slot:helpContent>
                  <p>This stock item will be used when a group of order items or a single order item falls below the minimum price charge. An order item with this stock code will be automatically added to cover the shortfall to reach the minimum price charge for a group of items or a single item.</p>
                  <p>If not set, no minimum charge fee will be added to orders.</p>
                </template>
                <template v-slot:entry>
                  <LinkStock v-if="!generalConfig.editing" :id="getConfigSettingInt(AccountSettingKeys.orderMinPriceFeeStockId)" :name="getLookupName(generalStockListLookup, getConfigSettingInt(AccountSettingKeys.orderMinPriceFeeStockId))" />
                  <FormInputStockCode v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderMinPriceFeeStockId)" @input="setConfigSetting(AccountSettingKeys.orderMinPriceFeeStockId, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Minimum Invoice Fee Stock Item" helpDefault="None">
                <template v-slot:helpContent>
                  <p>This stock item will be used when adding fees to cover shortfall for orders below the minimum invoice charge.</p>
                  <p>If not set, no minimum invoice charge will be added to orders.</p>
                </template>
                <template v-slot:entry>
                  <LinkStock v-if="!generalConfig.editing" :id="getConfigSettingInt(AccountSettingKeys.orderMinInvoiceFeeStockId)" :name="getLookupName(generalStockListLookup, getConfigSettingInt(AccountSettingKeys.orderMinInvoiceFeeStockId))" />
                  <FormInputStockCode v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderMinInvoiceFeeStockId)" @input="setConfigSetting(AccountSettingKeys.orderMinInvoiceFeeStockId, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Template Handling Stock Item" helpDefault="None">
                <template v-slot:helpContent>
                  <p>For order items with this template handling stock item as a service, a special process will be required in optimisation to handle template processing before cutting a sheet.</p>
                </template>
                <template v-slot:entry>
                  <LinkStock v-if="!generalConfig.editing" :id="getConfigSettingInt(AccountSettingKeys.orderTemplateHandlingStockId)" :name="getLookupName(glassServiceStockListLookup, getConfigSettingInt(AccountSettingKeys.orderTemplateHandlingStockId))" />
                  <FormInputStockCode v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderTemplateHandlingStockId)" @input="setConfigSetting(AccountSettingKeys.orderTemplateHandlingStockId, $event)" />
                </template>
              </FormAccountSetting>

            </div>
            <div class="col-lg-4">
              <FormAccountSetting label="Pre Production Enabled" helpDefault="True">
                <template v-slot:helpContent>
                  <p>Orders will go into a pre-production status prior to production if this setting is enabled. Pre-production is used to manually send emails and print labels / sheets before production.</p>
                  <p>Pre-production can be disabled for systems that have cutting optimisation available as the labels will be printed at cutting.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEnablePreProduction)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEnablePreProduction)" @change="setConfigSetting(AccountSettingKeys.orderEnablePreProduction, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Pre Production - Send Order Enabled" helpDefault="True" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enables the Send Order pre-production option for orders in pre-production status.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderPreProdEnableSendOrder)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderPreProdEnableSendOrder)" @change="setConfigSetting(AccountSettingKeys.orderPreProdEnableSendOrder, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Pre Production - Print Sheets Enabled" helpDefault="True" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enables the Print Sheets pre-production option for orders in pre-production status.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderPreProdEnablePrintSheets)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderPreProdEnablePrintSheets)" @change="setConfigSetting(AccountSettingKeys.orderPreProdEnablePrintSheets, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Pre Production - Print Labels Enabled" helpDefault="True" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enables the Print Labels pre-production option for orders in pre-production status.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderPreProdEnablePrintLabels)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderPreProdEnablePrintLabels)" @change="setConfigSetting(AccountSettingKeys.orderPreProdEnablePrintLabels, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Show Panel Preview in Documents" helpDefault="True" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enabling this value will show the panel preview in the order documents used for printing and emails.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderShowPreviewInDocument)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderShowPreviewInDocument)" @change="setConfigSetting(AccountSettingKeys.orderShowPreviewInDocument, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Force Production Available in Scan Despatch" helpDefault="False" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enabling this value make the option available to force complete production when manually scanning orders out for despatch.</p>
                  <p>This allows orders to be scanned off for both production and despatch in one action.<br />
                  Used for configurations that do not have station scanning available for production.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderDespatchScanForceProductionEnabled)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderDespatchScanForceProductionEnabled)" @change="setConfigSetting(AccountSettingKeys.orderDespatchScanForceProductionEnabled, $event)" />
                </template>
              </FormAccountSetting>
            </div>

            <div class="col-lg-4">
              <FormAccountSetting label="Same Day Order Cutoff Time" helpDefault="10:00am">
                <template v-slot:helpContent>
                  <p>Orders entered in before this time of day will be allocated the remainder of the day&apos;s production shifts for production estimate timing.</p>
                  <p>Orders entered in after this time of day will be allocated into production from the beginning of the next valid production day.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSetting(AccountSettingKeys.orderSameDayCutoffTime) | formatTimeFromTimeOnly}}</div>
                  <FormTime v-if="generalConfig.editing" :value="getConfigSetting(AccountSettingKeys.orderSameDayCutoffTime)" @input="setConfigSetting(AccountSettingKeys.orderSameDayCutoffTime, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Pre-Paid Grace Period" helpDefault="120mins">
                <template v-slot:helpContent>
                  <p>Orders requiring pre-payment will be given a grace period time from when the order was created to when the order needs to be paid to keep the date settings.</p>
                  <p>If the pre-payment is not settled within the grace period, some order settings will be defaulted. Including:<br />
                  <ul>
                    <li>If an earlier date was requested for the order, it'll be reset to default estimated date</li>
                    <li>Urgent option on the order (if set) will be removed</li>
                  </ul>
                  Set to zero to disable.
                  </p>
                </template>
                <template v-slot:entry>
                  <span v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.orderPrePaidGracePeriodTime)}} minutes</span>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderPrePaidGracePeriodTime)" @input="setConfigSetting(AccountSettingKeys.orderPrePaidGracePeriodTime, $event)" addon="mins" />                        
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Quote Expiry Time" helpDefault="30 days" class="mt-4">
                <template v-slot:helpContent>
                  <p>The number of days after a quote has been sent and waiting customer approval until it is expired and cancelled.<br />
                  Set to zero to disable quote expiration.</p>
                </template>
                <template v-slot:entry>
                  <span v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.orderQuoteExpiryDays)}} days</span>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderQuoteExpiryDays)" @input="setConfigSetting(AccountSettingKeys.orderQuoteExpiryDays, $event)" addon="days" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Custom Field List" helpDefault="None" class="mt-4">
                <template v-slot:helpContent>
                  <p>A list of custom fields available during order item editing and order item importing</p>
                </template>
                <template v-slot:entry>
                  <span v-if="!generalConfig.editing">{{getConfigSettingArray(AccountSettingKeys.orderCustomFieldList).join(', ')}}</span>
                  <FormMultiSelect v-if="generalConfig.editing" :taggable="true" placeholder="Custom Field List" :value="getConfigSettingArray(AccountSettingKeys.orderCustomFieldList)" @input="setConfigSettingArray(AccountSettingKeys.orderCustomFieldList, $event)" note="Press enter to add each field to the list" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Custom Field - QR Code" helpDefault="None" class="mt-4">
                <template v-slot:helpContent>
                  <p>The custom field to display as an additional QR code on the production label</p>
                </template>
                <template v-slot:entry>
                  <span v-if="!generalConfig.editing">{{getConfigSetting(AccountSettingKeys.orderCustomFieldQR) || 'None'}}</span>
                  <FormSelect v-if="generalConfig.editing" :options="customFieldQRLookup" placeholder="Custom Field QR" :value="getConfigSetting(AccountSettingKeys.orderCustomFieldQR)" @input="setConfigSetting(AccountSettingKeys.orderCustomFieldQR, $event)" note="Select the custom field or none to not display an additional QR code" />
                </template>
              </FormAccountSetting>

            </div>
          </div>
          <FormSubmit v-if="generalConfig.editing" label="Update" :submitting="generalConfig.submitting" @submitted="submitGeneralConfigSettings" @cancelled="cancelGeneralConfigSettings" />
        </FormGroup>
      </TabPage>

      <TabPage tabid="tab-activity-log">
        <div class="row">
          <div class="col-lg-12">
            <ActivityLogStream ref="activityStream" url="/orders/configuration/activitystream">
              <template v-slot:title><h3>Recent Activity</h3></template>
            </ActivityLogStream>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<style scoped>
.stamp-preview-image {
  max-width: 64px;
  max-height: 64px;
  overflow: clip;
}
</style>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import Utils from '@/utils/utils'
import form from '@/mixins/form'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import OrderStampConfigDialog from '@/modules/orders/views/orderStampConfigDialog'

import { InventoryStockTypes, AccountSettingKeys } from '@/store/types'
import { AlignmentTypeLookup } from '@/store/mathTypes'

export default {
  name: 'OrderConfiguration',
  mixins: [form],
  components: {
    FormInputStockCode,
  },
  data: () => {
    return {
      loading: false,
      loadingError: null,
      stockList: [],
      resourceGroupListLookup: [],
      generalConfig: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      },
      stampConfig: {
      }
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.AccountSettingKeys = AccountSettingKeys
    this.AlignmentTypeLookup = AlignmentTypeLookup
  },
  mounted() {
    this.requestData()
  },
  computed: {
    generalStockListLookup() {
      return this.$data.stockList.filter(stock => stock.type == InventoryStockTypes.general).map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },
    glassServiceStockListLookup() {
      return this.$data.stockList.filter(stock => stock.type == InventoryStockTypes.glassService).map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },
    resourceGroupLookup() {
      return this.$data.resourceGroupListLookup.map(resourceGroup => {
        return {
          value: resourceGroup.id,
          name: resourceGroup.name
        }
      })
    },
    customFieldQRLookup() {
      return _.concat(
        [{ value: '', name: 'None' }],
        this.getConfigSettingArray(AccountSettingKeys.orderCustomFieldList).map(field => {
        return {
          value: field,
          name: field
        }
      }))
    }
  },
  methods: {
    reloadData() {
      this.reloadActivity()
      this.requestData()
    },
    reloadActivity() {
      this.$refs.activityStream.reloadData()
    },
    requestData() {
      this.loadingError = false
      this.loading = true
      Promise.all([
        this.$apiService.ordersService.getConfigSettings(),
        this.$apiService.ordersService.getStampConfig(),
        this.$apiService.inventoryService.getStockList(),
        this.$apiService.tasksService.getResourceGroupList()        
      ])
      .then(results => {
        this.$data.generalConfig.settings = results[0].data
        this.$data.stampConfig = results[1].data
        this.$data.stockList = results[2].data
        this.$data.resourceGroupListLookup = results[3].data
      })
      .catch(err => {
        this.loadingError = 'Failed to load data'
      })
      .finally(() => {
        this.loading = false
      })     
    },

    // General Config Settings
    getConfigSetting(settingKey, defaultValue = '') {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.generalConfig.settings[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingKey, defaultValue = false) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.parseBoolean(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingKey, defaultValue = 0) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return parseInt(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingKey, defaultValue = 0) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.parseDecimal(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    getConfigSettingArray(settingKey, defaultValue = []) {
      if(this.generalConfig.settings[settingKey] !== undefined) {
        try {
          return JSON.parse(this.generalConfig.settings[settingKey])
        } catch(err) {
        }
      }
      return defaultValue
    },
    setConfigSetting(settingKey, value) {
      this.generalConfig.settings[settingKey] = value
    },
    setConfigSettingArray(settingKey, value) {
      this.generalConfig.settings[settingKey] = JSON.stringify(value || [])
    },

    submitGeneralConfigSettings() {
      this.$data.generalConfig.submitting = true
      this.$apiService.ordersService.updateConfigSettings({ settings: this.$data.generalConfig.settings })
      .then(response => {
        this.$data.generalConfig.editing = false
        this.successToast('Order Config Settings updated')
        this.reloadActivity()
      })
      .catch(err => {
        this.$data.generalConfig.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.generalConfig.submitting = false
      })
    },
    cancelGeneralConfigSettings() {
      this.$data.generalConfig.editing = false
      this.$apiService.ordersService.getConfigSettings()
      .then(response => {
        this.$data.generalConfig.settings = response.data
      })
    },

    editStampConfig() {
      this.displayModal(OrderStampConfigDialog)
      .then(result => {
        if(result) {
          this.successToast('Stamp Config Updated')
          this.reloadData()
        }
      })
    }

  }
}

</script>